'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  Wallet, CreditCard, TrendingUp, Building2, User, Truck,
  Loader2, Edit2, Check, X, ArrowRight, AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import {
  getAccountBalances,
  upsertAccountBalance,
  calculateNetWorth,
  type AccountBalance,
} from '@/lib/accountBalances';

function fmt(n: number) {
  return Math.abs(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

interface AccountRowProps {
  acc: AccountBalance;
  onSave: (id: string, accountId: string, value: number) => Promise<void>;
  saving: boolean;
}

function AccountRow({ acc, onSave, saving }: AccountRowProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const isLiability = acc.accountType === 'credit_card' || acc.accountType === 'loan';

  const startEdit = () => {
    setEditing(true);
    setEditValue(acc.balance === 0 ? '' : String(acc.balance));
  };

  const commit = async () => {
    const parsed = parseFloat(editValue.replace(/[^0-9.-]/g, ''));
    const newBal = isNaN(parsed) ? 0 : parsed;
    await onSave(acc.id, acc.accountId, newBal);
    setEditing(false);
    setEditValue('');
  };

  const cancel = () => { setEditing(false); setEditValue(''); };

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0 group">
      <span className="text-sm text-foreground truncate max-w-[140px]">{acc.accountName}</span>
      <div className="flex items-center gap-2 flex-shrink-0">
        {editing ? (
          <>
            <input
              autoFocus
              type="number"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
              className="w-28 bg-input border border-accent rounded px-2 py-1 text-sm text-right text-foreground outline-none"
              placeholder="0.00"
              disabled={saving}
            />
            <button onClick={commit} disabled={saving} className="text-success hover:text-success/80 transition-colors">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            </button>
            <button onClick={cancel} className="text-danger hover:text-danger/80 transition-colors">
              <X size={14} />
            </button>
          </>
        ) : (
          <>
            <span className={`text-sm font-600 font-mono ${isLiability ? 'text-danger' : acc.balance >= 0 ? 'text-foreground' : 'text-danger'}`}>
              {isLiability ? `-${fmt(acc.balance)}` : fmt(acc.balance)}
            </span>
            <button
              onClick={startEdit}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-accent"
            >
              <Edit2 size={12} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ManualAccountBalances() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<AccountBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAccountBalances(user.id);
      setAccounts(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load balances');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (id: string, accountId: string, value: number) => {
    if (!user) return;
    setSavingId(id);
    try {
      const updated = await upsertAccountBalance(user.id, accountId, value);
      setAccounts(prev => prev.map(a => a.id === updated.id ? updated : a));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="text-accent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-danger/10 border border-danger/20 text-danger text-sm">
        <AlertCircle size={14} />
        <span>{error}</span>
        <button onClick={load} className="ml-auto text-xs underline">Retry</button>
      </div>
    );
  }

  const summary = calculateNetWorth(accounts);

  const summaryCards = [
    { label: 'Total Cash', value: summary.totalCash, icon: <Wallet size={16} />, color: 'text-success', bg: 'bg-success/10 border-success/20' },
    { label: 'Total Debt', value: -summary.totalDebt, icon: <CreditCard size={16} />, color: 'text-danger', bg: 'bg-danger/10 border-danger/20' },
    { label: 'Net Worth', value: summary.netWorth, icon: <TrendingUp size={16} />, color: summary.netWorth >= 0 ? 'text-success' : 'text-danger', bg: summary.netWorth >= 0 ? 'bg-success/10 border-success/20' : 'bg-danger/10 border-danger/20' },
    { label: 'Business Cash', value: summary.businessCash, icon: <Building2 size={16} />, color: 'text-accent', bg: 'bg-accent/10 border-accent/20' },
    { label: 'Personal Cash', value: summary.personalCash, icon: <User size={16} />, color: 'text-foreground', bg: 'bg-muted border-border' },
    { label: 'Park-N-Lot', value: summary.parkNLotBudget, icon: <Truck size={16} />, color: 'text-warning', bg: 'bg-warning/10 border-warning/20' },
  ];

  const personalAccounts = accounts.filter(a => a.ownership === 'personal');
  const businessAccounts = accounts.filter(a => a.ownership === 'business');

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryCards.map(card => (
          <div key={card.label} className={`rounded-xl border p-4 flex flex-col gap-1.5 ${card.bg}`}>
            <div className={`flex items-center gap-1.5 ${card.color}`}>
              {card.icon}
              <span className="text-[10px] font-600 uppercase tracking-wide">{card.label}</span>
            </div>
            <p className={`text-lg font-700 font-mono ${card.color}`}>
              {card.value < 0 ? '-' : ''}{fmt(Math.abs(card.value))}
            </p>
          </div>
        ))}
      </div>

      {/* Account tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
            <User size={14} className="text-muted-foreground" />
            <h3 className="text-sm font-600 text-foreground">Personal Accounts</h3>
          </div>
          <div className="px-5 py-1">
            {personalAccounts.map(acc => (
              <AccountRow key={acc.id} acc={acc} onSave={handleSave} saving={savingId === acc.id} />
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
            <Building2 size={14} className="text-muted-foreground" />
            <h3 className="text-sm font-600 text-foreground">Business Accounts</h3>
          </div>
          <div className="px-5 py-1">
            {businessAccounts.map(acc => (
              <AccountRow key={acc.id} acc={acc} onSave={handleSave} saving={savingId === acc.id} />
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">
          Hover any row and click the edit icon to update. Saved to Supabase.
        </p>
        <Link
          href="/account-balances"
          className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 transition-colors font-500"
        >
          Manage Accounts <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
